/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import once from 'lodash.once';
import remove from 'lodash.remove';
import { EventCallbackDisposer } from 'src/core/types/eventBusEvent';
import { EventTable } from 'src/core/types/EventTable';

/**
 * @deprecated
 * Represents a system wide event bus that is primary used
 * for cross-handler communication. Since handlers should not
 * be using the dispatcher to call other handlers, we use a
 * separate construct to facilitate reactive firing of events
 * across the system. Examples of cross handler events include
 * toasts, insights tracking, logging, etc.
 */
export const getEventBus = once(() => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    const eventTable = new Map<string, Function[]>();

    /**
     * Registers a handler (callback) to a specific event name, such
     * that whenever this event occurs, the callbacks stored will
     * all be fired.
     *
     * @param eventName The event name to store as a key to the set of
     * callbacks to fire when this event occurs.
     * @param callback The callback to register with the array of existing
     * callbacks that will fire when the event is triggered.
     */
    const on = <K extends keyof EventTable>(eventName: K, callback: EventTable[K]): EventCallbackDisposer => {
        if (!eventTable.has(eventName)) {
            eventTable.set(eventName, []);
        }

        eventTable.get(eventName).push(callback);

        return () => {
            remove(eventTable.get(eventName), cb => cb === callback);
        };
    };

    /**
     * Emits or triggers the event with the given name and passes it the
     * given arguments. This causes all the callbacks registered to this
     * event in the event table map to be fired.
     *
     * @param eventName The event name to fire/trigger.
     * @param args The arguments to pass to the event.
     */
    const emit = <K extends keyof EventTable>(eventName: K, ...args: Parameters<EventTable[K]>): void => {
        const callbacksToFire = eventTable.get(eventName) || [];

        callbacksToFire.forEach(cb => {
            try {
                cb(...args);
            } catch (error) {
                throw error;
            }
        });
    };

    return { on, emit };
});
